import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";

export default function AdminDashboard() {
  const [lawyers, setLawyers] = useState<any[]>([]);
  const [casesByLawyer, setCasesByLawyer] = useState<{ [lawyerId: string]: any[] }>({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [unauthorized, setUnauthorized] = useState(false);


  useEffect(() => {
    const checkAdminAndFetchData = async () => {
      setLoading(true);

      // ✅ Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        alert("You must be logged in");
        navigate("/"); // redirect non-logged in users
        return;
      }

      // ✅ Fetch user's role from DB
      const { data: profile, error: profileError } = await supabase
        .from("users")
        .select("role")
        .eq("id", user.id)
        .single();

      if (profileError || !profile) {
        alert("Failed to verify user role");
        navigate("/");
        return;
      }

      if (profile.role !== "administrator") {
        setUnauthorized(true);
        setLoading(false);
        return;
      }


      // ✅ User is admin — fetch data
      const { data: lawyerData, error: lawyerError } = await supabase
        .from("users")
        .select("*")
        .eq("role", "lawyer");

      if (lawyerError) {
        console.error("Error fetching lawyers:", lawyerError);
        setLoading(false);
        return;
      }
      setLawyers(lawyerData || []);

      const { data: allCases, error: caseError } = await supabase
        .from("cases")
        .select("*");

      if (caseError) {
        console.error("Error fetching cases:", caseError);
        setLoading(false);
        return;
      }

      const casesMap: { [lawyerId: string]: any[] } = {};
      (allCases || []).forEach((c) => {
        const lawyerId = String(c.lawyer_id);
        if (!casesMap[lawyerId]) casesMap[lawyerId] = [];
        casesMap[lawyerId].push(c);
      });
      setCasesByLawyer(casesMap);
      setLoading(false);
    };

    checkAdminAndFetchData();

    // 🔄 Real-time subscriptions
    const lawyerChannel = supabase
      .channel("lawyers_channel")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "users", filter: "role=eq.lawyer" },
        (payload) => setLawyers((prev) => [...prev, payload.new])
      )
      .subscribe();

    const caseChannel = supabase
      .channel("cases_channel")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "cases" },
        (payload) => {
          const newCase = payload.new as any;
          const lawyerId = String(newCase.lawyer_id);
          setCasesByLawyer((prev) => {
            const existing = prev[lawyerId] || [];
            if (payload.eventType === "INSERT") {
              return { ...prev, [lawyerId]: [...existing, newCase] };
            }
            if (payload.eventType === "UPDATE") {
              return {
                ...prev,
                [lawyerId]: existing.map((c) => (c.id === newCase.id ? newCase : c)),
              };
            }
            if (payload.eventType === "DELETE") {
              return {
                ...prev,
                [lawyerId]: existing.filter((c) => c.id !== payload.old.id),
              };
            }
            return prev;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(lawyerChannel);
      supabase.removeChannel(caseChannel);
    };
  }, [navigate]);

  if (loading) return <div>Loading...</div>;

  if (unauthorized)
  return (
    <div>
      <h2>Access Denied</h2>
      <p>You do not have permission to view this page.</p>
      <button onClick={() => navigate("/")}>Go Home</button>
    </div>
  );

  return (
    <div>
      <h1>Admin Dashboard</h1>
      {lawyers.map((lawyer) => (
        <div key={lawyer.id} style={{ marginBottom: 20 }}>
          <details>
            <summary>
              {lawyer.name} ({casesByLawyer[String(lawyer.id)]?.length || 0} cases)
            </summary>
            <ul>
              {casesByLawyer[String(lawyer.id)]?.map((c) => (
                <li key={c.id}>{c.title}</li>
              ))}
            </ul>
          </details>
        </div>
      ))}
    </div>
  );
}
